import React from "react";

import type { DropdownProps } from "@noones/ui";
import { ActionItem, Dropdown, Popover, useDisclosure } from "@noones/ui";
import { IconChevronDown } from "@noones/ui/icons/mono-no-pixel";
import { tw } from "@noones/ui/tw";

import { selectSafeLocale, setLocale } from "../model/locale";
import { useDispatch, useSelector } from "react-redux";

const items: Record<
    string,
    {
        value: string;
        name: string;
        description?: string;
    }
> = {
    en: {
        value: "en",
        name: "English",
    },
    zh: {
        value: "zh",
        name: "简体中文(SC)",
        description: "Simplified Chinese",
    },
    "zh-hant": {
        value: "zh-hant",
        name: "繁體中文(TC)",
        description: "Traditional Chinese",
    },
    "pt-br": {
        value: "pt-br",
        name: "Português brasileiro",
        description: "Brazilian Portuguese",
    },
    ko: {
        value: "ko",
        name: "한국인",
        description: "Korean",
    },
    fr: {
        value: "fr",
        name: "Fran\u00e7ais",
        description: "French",
    },
    es: {
        value: "es",
        name: "Espa\u00f1ol",
        description: "Spanish",
    },
    ru: {
        value: "ru",
        name: "Русский",
        description: "Russian",
    },
    vi: {
        value: "vi",
        name: "Vietnamita",
        description: "Vietnamese",
    },
    ar: {
        value: "ar",
        name: "العربية",
        description: "Arabic",
    },
    pcm: {
        value: "pcm",
        name: "Naija",
        description: "Nigerian Pidgin",
    },
    sw: {
        value: "sw",
        name: "Kiswahili",
        description: "Swahili",
    },
};

type LanguagePickerProps = {
    dropdownProps?: Partial<DropdownProps>;
};

export const LanguagePicker = ({ dropdownProps }: LanguagePickerProps) => {
    const dispatch = useDispatch();

    const [isLanguageDropdownOpened, { toggle: toggleLanguageDropdown }] = useDisclosure();

    const locale = useSelector(selectSafeLocale);

    const handleChange = (value: string) => {
        dispatch(setLocale(value));
    };

    return (
        <Popover
            position="bottom-end"
            withinPortal={false}
            closeOnEscape={true}
            opened={isLanguageDropdownOpened}
            onChange={toggleLanguageDropdown}
            width={257}
            id="header-language-picker"
        >
            <Popover.Target>
                <Dropdown
                    mode="grey"
                    size={40}
                    onClick={toggleLanguageDropdown}
                    rightSection={<IconChevronDown width={16} height={16} />}
                    {...dropdownProps}
                >
                    {items[locale].name}
                </Dropdown>
            </Popover.Target>

            <Popover.Dropdown className={tw.bg_transparent.border_none.p_4}>
                {Object.values(items).map((item) => (
                    <ActionItem
                        key={item.value}
                        title={item.name}
                        description={item.description}
                        onClick={() => handleChange(item.value)}
                        active={locale === item.value}
                        className={tw.important(tw.no_underline)}
                    />
                ))}
            </Popover.Dropdown>
        </Popover>
    );
};
